import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  getErrorMessage,
  useGetIsLegalEntity,
  useGetOwners,
  useMutationRequest,
} from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import { patchDetails } from "~/v2/api/rest/account";
import { postAgreementForLegalEntityAndSigner } from "~/v2/api/rest/entityOnboarding";
import { Path } from "~/v2/path";

const CRYPTO_AGREEMENT_REVISION = "02.17.2023";
const CRYPTO_AGREEMENT_URL =
  "https://files.alpaca.markets/disclosures/library/Crypto%20Customer%20Agreement.pdf";

export interface UseAgreementProps {
  accountId: string;
  isCryptoAgreementSigned: boolean;
  isAccountLoading: boolean;
  isCryptoSupported: boolean;
  isCryptoActive: boolean;
}

export interface UseAgreementReturn {
  agreementUrl: string;
  isLoading: boolean;
  isModalOpen: boolean;
  isSettingUpAccount: boolean;
  onSignAgreement: () => void;
  onCancelAgreementModal: () => void;
}

export const useAgreement = ({
  accountId,
  isAccountLoading,
  isCryptoSupported,
  isCryptoAgreementSigned,
  isCryptoActive,
}: UseAgreementProps): UseAgreementReturn => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const isAgreementQueryEnabled = !isAccountLoading;
  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const { owners, isLoading: isOwnersLoading } = useGetOwners({
    accountId,
    options: {
      enabled:
        isAgreementQueryEnabled &&
        !!accountId &&
        isLegalEntity &&
        !isLegalEntityLoading,
    },
  });

  const legalEntityOwner = owners?.find((o) => o.is_legal_entity);
  const accountOpener = owners?.find((o) => o.is_legal_account_opener);

  const signMutation = useMutationRequest(
    async () =>
      isLegalEntity && legalEntityOwner && accountOpener
        ? postAgreementForLegalEntityAndSigner({
            ownerId: legalEntityOwner.owner_id,
            agreement: "crypto_agreement",
            accountId,
            signerPartyId: accountOpener.owner_id,
            revision: CRYPTO_AGREEMENT_REVISION,
          })
        : patchDetails(accountId, {
            crypto_agreement_signed_at: new Date(),
            crypto_agreement_revision: CRYPTO_AGREEMENT_REVISION,
          }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.accounts.details(accountId),
        });
        toastSuccess("Crypto agreement signed");
        AmplitudeProvider.dispatch(
          "crypto_transfers_crypto_agreement_finished",
        );
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error signing crypto agreement: ${errorMessage}`);
        Sentry.captureException(error);
      },
    },
  );

  const onSignAgreement = () => signMutation.mutate(true);

  const onCancelClicked = () => history.push(Path.ROUTE_DASHBOARD);

  const isModalOpen =
    !isAccountLoading &&
    isCryptoSupported &&
    (!isCryptoAgreementSigned || !isCryptoActive);

  const isSettingUpAccount =
    signMutation.isSuccess ||
    (!isAccountLoading &&
      isCryptoSupported &&
      isCryptoAgreementSigned &&
      !isCryptoActive);

  return {
    agreementUrl: CRYPTO_AGREEMENT_URL,
    isLoading: signMutation.isPending || (isLegalEntity && isOwnersLoading),
    isModalOpen,
    isSettingUpAccount,
    onSignAgreement,
    onCancelAgreementModal: onCancelClicked,
  };
};
